import { call, put, select, StrictEffect, takeEvery } from "redux-saga/effects";

import {
  ReadChaserSchema,
  ReadQueryCommentSchema,
  ReadQuerySchema
} from "@/jenesys-types/data/communication";
import { DataType } from "@/jenesys-types/data/enums";
import { AppError } from "@/jenesys-types/errors";

import { performDataAction } from "@/services/api";
import { parseCommentSuccess } from "@/store/features/commentParsing/commentParsing";
import { selectActiveClientId } from "@/store/features/companies/companies";
import { selectCompanyMembers } from "@/store/features/companyMembers/companyMembers";
import {
  fetchDocumentQueries,
  fetchDocumentQueriesFailure,
  fetchDocumentQueriesSuccess
} from "@/store/features/transactions/documentQueries/documentQueries";
import { toastError, toastSuccess } from "@/util/helpers";
import { parseCommentText } from "@/utils/commentParsing";

import {
  createChaser,
  CreateChaserAction,
  createChaserFailure,
  createChaserSuccess,
  createQuery,
  CreateQueryAction,
  createQueryFailure,
  createQuerySuccess,
  deleteQuery,
  DeleteQueryAction,
  deleteQueryFailure,
  deleteQuerySuccess,
  loadDocumentQueries,
  LoadDocumentQueriesAction,
  loadDocumentQueriesFailure,
  loadDocumentQueriesSuccess,
  loadQueries,
  LoadQueriesAction,
  loadQueriesFailure,
  loadQueriesSuccess,
  loadQueryDetails,
  loadQueryDetailsFailure,
  loadQueryDetailsSuccess,
  postQueryComment,
  postQueryCommentFailure,
  postQueryCommentSuccess,
  reassignQuery,
  ReassignQueryAction,
  reassignQueryFailure,
  reassignQuerySuccess,
  resolveQuery,
  ResolveQueryAction,
  resolveQueryFailure,
  resolveQuerySuccess
} from "./communicationsHub";

function* loadQueriesSaga(
  action: LoadQueriesAction
): Generator<StrictEffect, void, any> {
  const { companyId, search, queryType } = action.payload;

  // Only include query_type and search if they are non-empty values
  const queryParams: Record<string, string> = {};
  if (search) queryParams.search = search;
  if (queryType) queryParams.query_type = queryType;

  try {
    const response = yield call(
      performDataAction,
      DataType.CommunicationsQuery,
      "list",
      { companyId },
      queryParams
    );

    yield put(loadQueriesSuccess(response));
  } catch (error) {
    yield put(loadQueriesFailure(error as AppError));
  }
}

function* loadDocumentQueriesSaga(
  action: LoadDocumentQueriesAction
): Generator<StrictEffect, void, any> {
  const { companyId, documentId } = action.payload;

  try {
    const response = yield call(
      performDataAction,
      DataType.DocumentQuery,
      "list",
      { companyId, document_id: documentId }
    );

    yield put(loadDocumentQueriesSuccess(response));
  } catch (error) {
    yield put(loadDocumentQueriesFailure(error as AppError));
  }
}

function* createQuerySaga(
  action: CreateQueryAction
): Generator<StrictEffect, void, any> {
  const { companyId, queryData, onSuccess } = action.payload;

  try {
    const response = yield call(
      performDataAction,
      DataType.CommunicationsQuery,
      "create",
      { companyId },
      {},
      queryData
    );

    yield put(createQuerySuccess());
    toastSuccess("Query created successfully");
    if (onSuccess) onSuccess(response.id);
  } catch (error) {
    yield put(createQueryFailure(error as AppError));
    toastError("Failed to create new query");
  }
}

function* loadQueryDetailsSaga(
  action: ReturnType<typeof loadQueryDetails>
): Generator<StrictEffect, void, any> {
  const { companyId, queryId } = action.payload;
  try {
    const response: ReadQuerySchema = yield call(
      performDataAction,
      DataType.CommunicationsQuery,
      "retrieve",
      { companyId, query_id: queryId }
    );
    yield put(loadQueryDetailsSuccess(response));
  } catch (error) {
    yield put(loadQueryDetailsFailure(error as AppError));
    toastError("Failed to load query details", error);
  }
}

function* postQueryCommentSaga(
  action: ReturnType<typeof postQueryComment>
): Generator<StrictEffect, void, any> {
  const { companyId, queryId, comment, onSuccess } = action.payload;

  try {
    const companyMembers = yield select(selectCompanyMembers);
    const parsedComment = yield call(parseCommentText, comment, companyMembers);
    const response: ReadQueryCommentSchema = yield call(
      performDataAction,
      DataType.CommunicationsQuery,
      "createComment",
      { companyId, query_id: queryId },
      {},
      {
        comment,
        mentions: parsedComment.mentions,
        parsed_content: parsedComment
      }
    );

    yield put(postQueryCommentSuccess(response));
    yield put(parseCommentSuccess(response.id, parsedComment));

    toastSuccess("Comment posted");
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(postQueryCommentFailure(error as AppError));
    toastError("Failed to post comment", error);
  }
}

function* createChaserSaga(
  action: CreateChaserAction
): Generator<StrictEffect, void, any> {
  const { chaserData, onSuccess } = action.payload;
  const companyId = yield select(selectActiveClientId);

  try {
    const response: ReadChaserSchema = yield call(
      performDataAction,
      DataType.Chaser,
      "create",
      { companyId },
      {},
      chaserData
    );

    yield put(createChaserSuccess());
    if (onSuccess) onSuccess(response.id);
  } catch (error) {
    yield put(createChaserFailure(error as AppError));
    toastError("Failed to create new chaser");
  }
}

function* resolveQuerySaga(
  action: ResolveQueryAction
): Generator<StrictEffect, void, any> {
  const { companyId, queryId, onSuccess } = action.payload;

  try {
    yield call(performDataAction, DataType.CommunicationsQuery, "close", {
      companyId,
      query_id: queryId
    });

    yield put(resolveQuerySuccess());
    toastSuccess("Query resolved!");
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(resolveQueryFailure(error as AppError));
    toastError("Failed to resolve query");
  }
}

function* deleteQuerySaga(
  action: DeleteQueryAction
): Generator<StrictEffect, void, any> {
  const { companyId, queryId, onSuccess } = action.payload;

  try {
    yield call(performDataAction, DataType.CommunicationsQuery, "delete", {
      companyId,
      query_id: queryId
    });

    yield put(deleteQuerySuccess());
    toastSuccess("Query deleted successfully");

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    yield put(deleteQueryFailure(error as AppError));

    toastError("Failed to delete query");
  }
}

function* reassignQuerySaga(
  action: ReassignQueryAction
): Generator<StrictEffect, void, any> {
  const { companyId, queryId, assignedToId, onSuccess } = action.payload;

  try {
    yield call(
      performDataAction,
      DataType.CommunicationsQuery,
      "update",
      {
        companyId,
        query_id: queryId
      },
      {},
      {
        assigned_to_user_id: assignedToId
      }
    );

    yield put(reassignQuerySuccess());
    toastSuccess("Query reassigned successfully");

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    yield put(reassignQueryFailure(error as AppError));

    toastError("Failed to reassign query");
  }
}

function* fetchDocumentQueriesSaga(
  action: ReturnType<typeof fetchDocumentQueries>
): Generator<StrictEffect, void, any> {
  const { companyId, documentId } = action.payload;

  try {
    const response = yield call(
      performDataAction,
      DataType.CommunicationsQuery,
      "listByDocument",
      { companyId, document_id: documentId }
    );

    yield put(fetchDocumentQueriesSuccess(response));
  } catch (error) {
    yield put(fetchDocumentQueriesFailure(error as AppError));
  }
}

export default function* CommunicationsHubSaga() {
  yield takeEvery(loadQueries.type, loadQueriesSaga);
  yield takeEvery(createQuery.type, createQuerySaga);
  yield takeEvery(loadQueryDetails.type, loadQueryDetailsSaga);
  yield takeEvery(postQueryComment.type, postQueryCommentSaga);
  yield takeEvery(createChaser.type, createChaserSaga);
  yield takeEvery(loadDocumentQueries.type, loadDocumentQueriesSaga);
  yield takeEvery(resolveQuery.type, resolveQuerySaga);
  yield takeEvery(deleteQuery.type, deleteQuerySaga);
  yield takeEvery(reassignQuery.type, reassignQuerySaga);
  yield takeEvery(fetchDocumentQueries.type, fetchDocumentQueriesSaga);
}
